import React, { useContext, createContext, useState, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from 'react-query';

import { getCssFileApiClient } from 'lib/SplashApiClient/subclients/CssFileApiClient';

export const CustomCssContext = createContext();

export const CustomCssContextProvider = ({ children, eventId, section, isEditing, isCmsCodeEnabled }) => {
    const [customCss, setCustomCss] = useState('');
    const cssFilesApiClient = useRef(getCssFileApiClient(eventId)).current;

    const {
        data = {},
        isLoading,
        isError,
    } = useQuery(
        ['customCssFileStructure', eventId, section],
        () => cssFilesApiClient.getCssFileBySection(section, isEditing),
        {
            select: (response) => response,
            enabled: isCmsCodeEnabled,
        },
    );

    useMemo(() => {
        if (!isCmsCodeEnabled || !data || isLoading || isError) {
            return;
        }

        const customCssDocument = isEditing ? data?.document : data;
        if (!customCssDocument) {
            return;
        }

        setCustomCss(customCssDocument);
    }, [isLoading, isError]);

    const saveCode = (value, userId) => {
        cssFilesApiClient.saveCssFiles({ section, userId, document: value }).then(() => {
            setCustomCss(value);
        });
    };

    const value = { isLoading, isError, customCss, saveCode };
    return <CustomCssContext.Provider value={value}>{children}</CustomCssContext.Provider>;
};

CustomCssContextProvider.propTypes = {
    children: PropTypes.node,
    section: PropTypes.string,
    isEditing: PropTypes.bool,
    eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    isCmsCodeEnabled: PropTypes.bool,
};

CustomCssContextProvider.defaultProps = {
    children: null,
    section: '',
    isEditing: false,
    isCmsCodeEnabled: false,
};

export const useCustomCss = () => useContext(CustomCssContext);
